import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import type {
  CompanyManageableRole,
  CompanyMembersQueryDTO,
  PaginatedCompanyMembersDTO,
} from '@shared/schemas/organization';
import { toast } from 'sonner';
import {
  changeCompanyMemberRole,
  resendCompanyMemberInvitation,
  setCompanyMemberActive,
  type CompanyMembersScope,
} from './company-api';
import { getCompanyErrorMessage } from './company-errors';
import { updateCompanyMemberPage } from './company-members-cache';
import { companyMembersQueryKeys } from './company-members-query';

export function useCompanyMemberMutations(
  scope: CompanyMembersScope,
  query: CompanyMembersQueryDTO,
  queryKey: QueryKey,
) {
  const queryClient = useQueryClient();
  function updateCachedPage(
    targetQueryKey: QueryKey,
    userId: string,
    change: Parameters<typeof updateCompanyMemberPage>[3],
  ) {
    queryClient.setQueryData<PaginatedCompanyMembersDTO>(targetQueryKey, (previous) => (
      previous && updateCompanyMemberPage(previous, query, userId, change)
    ));
  }
  const roleMutation = useMutation({
    mutationFn: ({ nextRole, userId }: { nextRole: CompanyManageableRole; userId: string }) => (
      changeCompanyMemberRole(userId, nextRole, scope)
    ),
    onMutate: async (variables) => {
      const mutatedQueryKey = queryKey;
      await queryClient.cancelQueries({ queryKey: mutatedQueryKey });
      const previous = queryClient.getQueryData<PaginatedCompanyMembersDTO>(mutatedQueryKey);
      updateCachedPage(mutatedQueryKey, variables.userId, {
        role: variables.nextRole,
        type: 'role',
      });
      return { mutatedQueryKey, previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.mutatedQueryKey, context.previous);
      }
      toast.error(getCompanyErrorMessage(error));
    },
    onSuccess: () => toast.success('Rôle du membre mis à jour.'),
    onSettled: () => queryClient.invalidateQueries({
      queryKey: companyMembersQueryKeys.lists(scope),
    }),
  });
  const statusMutation = useMutation({
    mutationFn: ({ active, userId }: { active: boolean; userId: string }) => (
      setCompanyMemberActive(userId, active, scope)
    ),
    onMutate: async (variables) => {
      const mutatedQueryKey = queryKey;
      await queryClient.cancelQueries({ queryKey: mutatedQueryKey });
      const previous = queryClient.getQueryData<PaginatedCompanyMembersDTO>(mutatedQueryKey);
      updateCachedPage(mutatedQueryKey, variables.userId, {
        active: variables.active,
        type: 'status',
      });
      return { mutatedQueryKey, previous };
    },
    onError: (error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.mutatedQueryKey, context.previous);
      }
      toast.error(getCompanyErrorMessage(error));
    },
    onSuccess: (_data, variables) => toast.success(
      variables.active ? 'Membre réactivé.' : 'Membre désactivé.',
    ),
    onSettled: () => queryClient.invalidateQueries({
      queryKey: companyMembersQueryKeys.lists(scope),
    }),
  });
  const invitationMutation = useMutation({
    mutationFn: (userId: string) => resendCompanyMemberInvitation(userId, scope),
    onError: (error) => toast.error(getCompanyErrorMessage(error)),
    onSuccess: () => toast.success('E-mail d’activation renvoyé.'),
  });

  return {
    changeRole: (userId: string, nextRole: CompanyManageableRole) => (
      roleMutation.mutate({ nextRole, userId })
    ),
    changeStatus: (userId: string, active: boolean) => (
      statusMutation.mutate({ active, userId })
    ),
    isPending: roleMutation.isPending || statusMutation.isPending || invitationMutation.isPending,
    resendInvitation: (userId: string) => invitationMutation.mutate(userId),
  };
}
